import PropTypes from 'prop-types'
import { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { DateTime } from 'luxon'

import { getParsedToken } from 'models/Redemptions/selectors'
import ScreenContainer from 'components/ScreenContainer'

import { STRING, CRYPTO_KEYS } from 'resources'

import useCurrentDateTime from 'hooks/useCurrentDateTime'
import formatCrypto from 'utils/format-crypto'
import formatPoints from 'utils/format-points'

import {
  RateExpirationText,
  RateExpirationTextRed,
  RateExpirationSection,
  HeroContainer,
  TextSectionContainer,
  TitleHeaderContainer,
  TitleHeader,
} from './styles'

const CRYPTO_PER_AMOUNT_DENOMINATOR = 1
const CRYPTO_SCALE = {
  [CRYPTO_KEYS.ETHEREUM]: 10,
  [CRYPTO_KEYS.BITCOIN]: 8,
  [CRYPTO_KEYS.USDC]: 6,
}

const CryptoExpirationRateBar = ({ setHasTimeExpired }) => {
  const currentTime = useCurrentDateTime()
  const parsedToken = useSelector(getParsedToken)

  const { crypto, quoteCryptoPerAmount, expTimestamp } = useMemo(
    () => ({
      crypto: parsedToken?.crypto ?? null,
      quoteCryptoPerAmount: parsedToken?.quoteCryptoPerAmount ?? 0,
      expTimestamp: parsedToken?.exp ?? 0,
    }),
    [parsedToken],
  )
  const expirationTime = useMemo(() => expTimestamp && DateTime.fromSeconds(expTimestamp), [expTimestamp])

  const formattedBasePoints = useMemo(() => formatPoints(CRYPTO_PER_AMOUNT_DENOMINATOR, false, true).toUpperCase(), [])
  const formattedCryptoQuote = useMemo(
    () => formatCrypto(quoteCryptoPerAmount, crypto, true, CRYPTO_SCALE[crypto]).toUpperCase(),
    [quoteCryptoPerAmount, crypto],
  )

  const remainingTime = DateTime.fromMillis(Math.max(0, expirationTime - currentTime))

  useEffect(() => {
    if (expirationTime !== null && remainingTime.minute <= 0 && remainingTime.second <= 0) {
      setHasTimeExpired(true)
    }
  }, [expirationTime, remainingTime, setHasTimeExpired])

  return (
    <HeroContainer>
      <ScreenContainer>
        <TextSectionContainer>
          <TitleHeaderContainer>
            <div>
              <TitleHeader>{STRING.wallet.redemptions.giftCardHero.italicHeader}</TitleHeader>
              <TitleHeader>
                <span>{formattedCryptoQuote}</span>
                <span>{STRING.cryptoRedeemScreen.slashBar}</span>
                <span>{formattedBasePoints}</span>
              </TitleHeader>
            </div>
            <RateExpirationSection>
              <RateExpirationText>
                {STRING.cryptoRedeemScreen.rateExpiresIn}
                <RateExpirationTextRed>{remainingTime.toFormat('mm:ss')}</RateExpirationTextRed>
              </RateExpirationText>
            </RateExpirationSection>
          </TitleHeaderContainer>
        </TextSectionContainer>
      </ScreenContainer>
    </HeroContainer>
  )
}

CryptoExpirationRateBar.propTypes = {
  setHasTimeExpired: PropTypes.func.isRequired,
}

export default CryptoExpirationRateBar
